import React, { useState, useEffect, useCallback } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SeriesCard from '../components/SeriesCard';
import { useLanguage } from '../context/LanguageContext';
import { tvApi, languageToTMDB } from '../services/api';
import { Tv, ChevronLeft, ChevronRight, Search, X, Filter } from 'lucide-react';

const SeriesPage = () => {
  const { t, language } = useLanguage();
  const [series, setSeries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeSearch, setActiveSearch] = useState('');
  const [providers, setProviders] = useState([]);
  const [selectedProvider, setSelectedProvider] = useState(null);
  const [viewMode, setViewMode] = useState('popular'); // popular, trending, top_rated, on_the_air
  const [showFilters, setShowFilters] = useState(false);

  // Fetch providers on mount
  useEffect(() => {
    const fetchProviders = async () => {
      try {
        const data = await tvApi.getProviders();
        setProviders(data.providers || []);
      } catch (error) {
        console.error('Error fetching providers:', error);
      }
    };
    fetchProviders();
  }, []);

  const fetchSeries = useCallback(async (pageNum, query = '', providerId = null, mode = 'popular') => {
    try {
      setLoading(true);
      const tmdbLang = languageToTMDB[language] || 'en-US';
      
      let data;
      if (query && query.length >= 2) {
        data = await tvApi.search(query, pageNum, tmdbLang);
      } else if (providerId) {
        data = await tvApi.getByNetwork(providerId, pageNum, tmdbLang);
      } else {
        switch (mode) {
          case 'trending':
            data = await tvApi.getTrending('week', pageNum, tmdbLang);
            break;
          case 'top_rated':
            data = await tvApi.getTopRated(pageNum, tmdbLang);
            break;
          case 'on_the_air':
            data = await tvApi.getOnTheAir(pageNum, tmdbLang);
            break;
          default:
            data = await tvApi.getPopular(pageNum, tmdbLang);
        }
      }
      
      setSeries(data.results || []);
      setTotalPages(Math.min(data.total_pages || 1, 50));
    } catch (error) {
      console.error('Error fetching series:', error);
    } finally {
      setLoading(false);
    }
  }, [language]);

  useEffect(() => {
    fetchSeries(page, activeSearch, selectedProvider, viewMode);
  }, [page, activeSearch, selectedProvider, viewMode, fetchSeries]);

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    setSelectedProvider(null);
    setActiveSearch(searchQuery);
  };

  const clearSearch = () => {
    setSearchQuery('');
    setActiveSearch('');
    setPage(1);
  };

  const handleProviderClick = (providerId) => {
    setSelectedProvider(providerId === selectedProvider ? null : providerId);
    setActiveSearch('');
    setSearchQuery('');
    setPage(1);
  };

  const handleViewModeChange = (mode) => {
    setViewMode(mode);
    setSelectedProvider(null);
    setActiveSearch('');
    setSearchQuery('');
    setPage(1);
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setPage(newPage);
      window.scrollTo(0, 0);
    }
  };

  const getPageNumbers = () => {
    const pages = [];
    const maxVisible = 5;
    let start = Math.max(1, page - Math.floor(maxVisible / 2));
    let end = Math.min(totalPages, start + maxVisible - 1);
    if (end - start + 1 < maxVisible) {
      start = Math.max(1, end - maxVisible + 1);
    }
    for (let i = start; i <= end; i++) {
      pages.push(i);
    }
    return pages;
  };

  const viewModes = [
    { id: 'popular', label: t('series.popular') || 'Popular' },
    { id: 'trending', label: t('series.trending') || 'Trending' },
    { id: 'top_rated', label: t('series.topRated') || 'Top Rated' },
    { id: 'on_the_air', label: t('series.onTheAir') || 'On The Air' },
  ];

  return (
    <div className="bg-[#0B0F0E] min-h-screen">
      <Header />

      <main className="pt-32 pb-20">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-[7.6923%]">
          {/* Page Header */}
          <div className="flex items-center gap-4 mb-8">
            <Tv className="w-10 h-10 text-[#52ea88]" />
            <div>
              <h1 className="text-4xl md:text-5xl font-semibold text-[#E6FFF6]">
                {t('series.pageTitle') || 'TV Series'}
              </h1>
              <p className="text-[#9FBFB4] mt-2 text-lg">
                {t('series.pageDescription') || 'Explore shows from Netflix, Disney+, HBO, and more'}
              </p>
            </div>
          </div>

          {/* Search Bar */}
          <form onSubmit={handleSearch} className="mb-6">
            <div className="relative max-w-xl">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#9FBFB4]" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder={t('series.searchPlaceholder') || 'Search TV series...'}
                className="w-full pl-12 pr-24 py-4 bg-[#121917] border border-[#52ea88]/20 rounded-lg text-[#E6FFF6] placeholder-[#9FBFB4]/50 focus:outline-none focus:border-[#52ea88]/50 transition-colors"
              />
              {searchQuery && (
                <button
                  type="button"
                  onClick={clearSearch}
                  className="absolute right-20 top-1/2 -translate-y-1/2 p-1 text-[#9FBFB4] hover:text-[#E6FFF6] transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              )}
              <button
                type="submit"
                className="absolute right-2 top-1/2 -translate-y-1/2 px-4 py-2 bg-[#52ea88] text-[#0B0F0E] font-semibold rounded-md hover:bg-[#52ea88]/90 transition-colors"
              >
                {t('series.search') || 'Search'}
              </button>
            </div>
          </form>

          {/* View Mode Tabs & Filter Toggle */}
          <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
            <div className="flex flex-wrap gap-2">
              {viewModes.map((mode) => (
                <button
                  key={mode.id}
                  onClick={() => handleViewModeChange(mode.id)}
                  disabled={activeSearch}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    viewMode === mode.id && !activeSearch && !selectedProvider
                      ? 'bg-[#52ea88] text-[#0B0F0E]'
                      : 'bg-[#1a2420] text-[#9FBFB4] hover:text-[#E6FFF6] disabled:opacity-50'
                  }`}
                >
                  {mode.label}
                </button>
              ))}
            </div>
            
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                showFilters || selectedProvider
                  ? 'bg-[#52ea88] text-[#0B0F0E]'
                  : 'bg-[#1a2420] text-[#9FBFB4] hover:text-[#E6FFF6]'
              }`}
            >
              <Filter className="w-4 h-4" />
              {t('series.streamingServices') || 'Streaming Services'}
            </button>
          </div>

          {/* Streaming Services Filter */}
          {showFilters && (
            <div className="mb-8 p-4 bg-[#121917] rounded-lg border border-[#52ea88]/10">
              <p className="text-sm text-[#9FBFB4] mb-3">{t('series.filterByService') || 'Filter by streaming service:'}</p>
              <div className="flex flex-wrap gap-3">
                {providers.map((provider) => (
                  <button
                    key={provider.id}
                    onClick={() => handleProviderClick(provider.id)}
                    className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors ${
                      selectedProvider === provider.id
                        ? 'bg-[#52ea88] text-[#0B0F0E]'
                        : 'bg-[#1a2420] text-[#9FBFB4] hover:text-[#E6FFF6] hover:bg-[#1a2420]/80'
                    }`}
                  >
                    {provider.logo && (
                      <img src={provider.logo} alt={provider.name} className="w-5 h-5 rounded" />
                    )}
                    {provider.name}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Active Search/Filter Indicator */}
          {(activeSearch || selectedProvider) && (
            <div className="mb-6 flex items-center gap-3">
              <p className="text-[#9FBFB4]">
                {activeSearch ? (
                  <>
                    {t('series.searchingFor') || 'Showing results for'}: 
                    <span className="text-[#52ea88] font-medium ml-2">"{activeSearch}"</span>
                  </>
                ) : selectedProvider ? (
                  <>
                    {t('series.filteringBy') || 'Filtering by'}: 
                    <span className="text-[#52ea88] font-medium ml-2">
                      {providers.find(p => p.id === selectedProvider)?.name}
                    </span>
                  </>
                ) : null}
              </p>
              <button
                onClick={() => {
                  clearSearch();
                  setSelectedProvider(null);
                }}
                className="text-sm text-[#52ea88] hover:text-[#E6FFF6] underline transition-colors"
              >
                {t('series.clearFilter') || 'Clear filter'}
              </button>
            </div>
          )}

          {/* Loading State */}
          {loading && series.length === 0 && (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-6">
              {[...Array(10)].map((_, i) => (
                <div key={i} className="aspect-[2/3] bg-[#1a2420] rounded-lg animate-pulse" />
              ))}
            </div>
          )}

          {/* Series Grid */}
          {!loading && series.length > 0 && (
            <>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-6">
                {series.map((show) => (
                  <SeriesCard key={show.id} series={show} />
                ))}
              </div>

              {/* Pagination */}
              <div className="flex items-center justify-center gap-2 mt-12">
                <button
                  onClick={() => handlePageChange(page - 1)}
                  disabled={page === 1}
                  className="p-2 rounded-lg border border-[#52ea88]/30 text-[#52ea88] hover:bg-[#52ea88]/10 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>

                {getPageNumbers().map((pageNum) => (
                  <button
                    key={pageNum}
                    onClick={() => handlePageChange(pageNum)}
                    className={`px-4 py-2 rounded-lg border transition-colors ${
                      pageNum === page
                        ? 'bg-[#52ea88] text-[#0B0F0E] border-[#52ea88] font-semibold'
                        : 'border-[#52ea88]/30 text-[#9FBFB4] hover:text-[#E6FFF6] hover:bg-[#52ea88]/10'
                    }`}
                  >
                    {pageNum}
                  </button>
                ))}

                <button
                  onClick={() => handlePageChange(page + 1)}
                  disabled={page === totalPages}
                  className="p-2 rounded-lg border border-[#52ea88]/30 text-[#52ea88] hover:bg-[#52ea88]/10 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                >
                  <ChevronRight className="w-5 h-5" />
                </button>
              </div>

              <p className="text-center text-[#9FBFB4] text-sm mt-4">
                {t('series.page') || 'Page'} {page} {t('series.of') || 'of'} {totalPages}
              </p>
            </>
          )}

          {/* No Results */}
          {!loading && series.length === 0 && (
            <div className="text-center py-20">
              <Tv className="w-16 h-16 text-[#52ea88]/30 mx-auto mb-4" />
              <p className="text-[#9FBFB4]/70 text-lg">
                {t('series.noResults') || 'No series found'}
              </p>
            </div>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default SeriesPage;
